import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import apiClient, { ApiClientError } from '@/api/client'
import {
  normalizeEnabledOrderTypes,
  enabledIdsFromConfig,
  isMarketplaceSettledType,
  type OrderTypeConfig,
  type OrderTypeTaxTreatment,
} from '@/lib/orderTypeSettings'
import { useMarketplaceSettledEnabled } from '@/hooks/useMarketplaceSettledEnabled'

export type { OrderTypeConfig } from '@/lib/orderTypeSettings'

export interface OrderTypePricing {
  includeServiceCharge: boolean
  deliveryFee: number
  /**
   * F-MM-N23. 'marketplace_settled' only when the tenant feature flag is on
   * AND the type is custom AND the row says so; everything else is 'standard'.
   * For an already-created order use the server-stamped `order.tax_treatment`
   * instead — settings may have changed since it was rung.
   */
  taxTreatment: OrderTypeTaxTreatment
}

/** Resolves per-type service and delivery defaults (aligned with backend order_types_guard). */
export function getOrderTypePricing(
  type: string,
  raw: OrderTypeConfig[],
  opts?: { marketplaceSettledEnabled?: boolean }
): OrderTypePricing {
  const tid = String(type ?? '').trim().toLowerCase()
  const row = raw.find((r) => String(r.id ?? '').trim().toLowerCase() === tid)
  const includeServiceCharge = row ? row.include_service_charge !== false : true
  const deliveryFee =
    tid === 'delivery' && row && typeof row.delivery_fee === 'number' && !Number.isNaN(row.delivery_fee)
      ? Math.max(0, row.delivery_fee)
      : 0
  const taxTreatment: OrderTypeTaxTreatment =
    opts?.marketplaceSettledEnabled === true && isMarketplaceSettledType(row)
      ? 'marketplace_settled'
      : 'standard'
  return { includeServiceCharge, deliveryFee, taxTreatment }
}

export interface UseEnabledOrderTypesResult {
  /** All enabled order type ids (built-in + custom) */
  enabledIds: ReadonlySet<string>
  isLoading: boolean
  /** Normalized merge of defaults + persisted rows */
  raw: OrderTypeConfig[]
  /** F-MM-N23 tenant flag — pass to getOrderTypePricing so marketplace rows take effect. */
  marketplaceSettledEnabled: boolean
}

/** The one query key every reader of app_settings.enabled_order_types shares. */
export const ENABLED_ORDER_TYPES_KEY = ['settings', 'enabled_order_types'] as const

/**
 * Read app_settings.enabled_order_types. Every reader of
 * ENABLED_ORDER_TYPES_KEY must use this, because they share one cache entry.
 *
 * - saved → the rows
 * - never saved → [] (the server answers 404, and "not configured" is a real
 *   answer, not a failure)
 * - network failure / 5xx → THROWS, so a transient failure never overwrites
 *   the last good copy (F-MM-N29, Karo's offline service charge)
 *
 * F-FE-N167: F-MM-N29 shipped the throw without the 404 case. Tenants that never
 * saved order types (Parched) then had an errored cache entry, and the guest
 * receipt — which fetches this key alongside the order — failed to print.
 */
export async function fetchEnabledOrderTypes(api: typeof apiClient = apiClient): Promise<OrderTypeConfig[]> {
  try {
    const res = await api.getSetting('enabled_order_types')
    if (res.success && Array.isArray(res.data)) return res.data as OrderTypeConfig[]
    if (res.success) return []
    throw new Error(res.message || 'Could not load order types')
  } catch (e) {
    if (e instanceof ApiClientError && e.status === 404) return []
    throw e
  }
}

/**
 * Which order types are enabled in Admin Settings (counter + server toggles).
 */
export function useEnabledOrderTypes(): UseEnabledOrderTypesResult {
  const { data, isLoading } = useQuery<OrderTypeConfig[]>({
    queryKey: ENABLED_ORDER_TYPES_KEY,
    // F-MM-N29 / F-FE-N167: see fetchEnabledOrderTypes — a transient failure
    // throws (keeping the last good copy); "never configured" is [].
    queryFn: () => fetchEnabledOrderTypes(),
  })

  const { enabled: marketplaceSettledEnabled } = useMarketplaceSettledEnabled()
  const raw = useMemo(() => normalizeEnabledOrderTypes(data ?? []), [data])

  const enabledIds = useMemo<ReadonlySet<string>>(() => {
    if (!data || data.length === 0) {
      return enabledIdsFromConfig([])
    }
    return enabledIdsFromConfig(raw)
  }, [data, raw])

  return {
    enabledIds,
    isLoading,
    raw,
    marketplaceSettledEnabled,
  }
}
